import React from 'react'
import { Link } from 'react-router-dom'
import { FaUsers, FaTrophy, FaStore, FaShieldAlt, FaCheckCircle, FaGift, FaArrowRight } from 'react-icons/fa'
import { Badge } from '../ui'
import CountdownTimer from './CountdownTimer'

// GiveawayCard — Phase 6A polish
//
// • Trust/verified chip for public-visible giveaways (backend already filters
//   to organizerType=platform OR organizerType=shop+approvalStatus=approved,
//   so anything we render here is already trust-worthy — we just surface it).
// • Prize preview row (first prize title) when available.
// • "Mağaza çəkilişi" eyebrow only when organizerType === 'shop'. Shop name
//   is rendered only if the public API returned it — we never invent one.
// • Dual CTA on active giveaways: primary "İştirak et" + secondary "Ətraflı".
//   The participate flow still lives on the detail page (auth-gated there),
//   so the card link in both cases routes to /cekilis/:slug.

const STATUS = {
    active:    { tone: 'success',     label: 'Aktiv' },
    ended:     { tone: 'warningSoft', label: 'Bitib' },
    completed: { tone: 'brandSoft',   label: 'Tamamlanıb' },
}

// variant: 'public' (default, 16:9 cover for /cekilisler) | 'home'
// (fixed compact cover height so homepage cards never grow tall).
const GiveawayCard = ({ giveaway, variant = 'public' }) => {
    if (!giveaway) return null
    const coverCls = variant === 'home'
        ? 'relative h-40 sm:h-36 overflow-hidden bg-gradient-to-br from-brand-100 via-brand-200 to-brand-300'
        : 'relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-brand-100 via-brand-200 to-brand-300'
    const target = `/cekilis/${encodeURIComponent(giveaway.slug)}`
    const cfg = STATUS[giveaway.status] || { tone: 'neutral', label: giveaway.status || '—' }
    const showCountdown = giveaway.status === 'active' && giveaway.endDate
    const isPast = giveaway.status === 'ended' || giveaway.status === 'completed'
    const isShop = giveaway.organizerType === 'shop'

    // Prize preview — show the title of the first prize if the API gave us one.
    const firstPrize = Array.isArray(giveaway.prizes) ? giveaway.prizes[0] : null
    const prizeText = firstPrize?.title?.trim() || ''

    // Shop name: only render when provided by the API; never invent.
    // Public API may attach `shopName` / `organizer.name` — both supported.
    const shopName = (giveaway.shopName || giveaway.organizer?.name || '').trim()

    return (
        <Link
            to={target}
            className={`group flex h-full flex-col overflow-hidden rounded-card border bg-white shadow-card transition-all hover:shadow-card-hover hover:-translate-y-0.5 ${
                isPast ? 'border-slate-200 opacity-95 saturate-75' : 'border-slate-200'
            }`}
        >
            {/* Cover */}
            <div className={coverCls}>
                {giveaway.coverImage ? (
                    <img
                        src={giveaway.coverImage}
                        alt={giveaway.title}
                        loading='lazy'
                        decoding='async'
                        className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                    />
                ) : (
                    <div className='flex h-full w-full items-center justify-center text-brand-700'>
                        <FaTrophy size={40} className='opacity-70' />
                    </div>
                )}

                {/* Top-left: organizer + trust */}
                <div className='absolute left-2 top-2 z-10 flex flex-wrap items-center gap-1.5'>
                    <Badge
                        tone={isShop ? 'accent' : 'brand'}
                        size='sm'
                        icon={isShop ? <FaStore size={9} /> : <FaShieldAlt size={9} />}
                    >
                        {isShop ? 'Mağaza' : 'Platforma'}
                    </Badge>
                    {/* Verified chip — shown for all public-visible giveaways
                        because the backend already filters to platform OR
                        approved shop. Lightweight visual trust signal. */}
                    <Badge tone='successSoft' size='sm' icon={<FaCheckCircle size={9} />}>
                        Təsdiqlənmiş
                    </Badge>
                </div>

                {/* Top-right: status */}
                <div className='absolute right-2 top-2 z-10'>
                    <Badge tone={cfg.tone} size='sm'>{cfg.label}</Badge>
                </div>

                {/* Bottom countdown */}
                {showCountdown && (
                    <div className='absolute bottom-2 left-2 z-10'>
                        <CountdownTimer targetDate={giveaway.endDate} variant='compact' />
                    </div>
                )}
            </div>

            {/* Body */}
            <div className='flex flex-1 flex-col gap-2 p-4'>
                {/* Shop eyebrow — only when shop-origin */}
                {isShop && (
                    <p className='text-[10px] font-semibold uppercase tracking-wider text-accent-700 inline-flex items-center gap-1'>
                        <FaStore size={9} />
                        {shopName ? `Mağaza çəkilişi · ${shopName}` : 'Mağaza çəkilişi'}
                    </p>
                )}

                <h3 className='line-clamp-2 text-sm font-bold text-slate-900 group-hover:text-brand-700 transition-colors'>
                    {giveaway.title}
                </h3>

                {/* Prize preview — only when the API gave us a first prize. */}
                {prizeText && (
                    <p className='inline-flex items-center gap-1.5 text-[11px] text-slate-600 line-clamp-1'>
                        <FaGift className='text-warning-500 shrink-0' size={10} />
                        <span className='font-semibold text-slate-700 truncate'>{prizeText}</span>
                    </p>
                )}

                {giveaway.description && !prizeText && (
                    <p className='line-clamp-2 text-xs text-slate-500'>{giveaway.description}</p>
                )}

                <div className='mt-auto flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500'>
                    <span className='inline-flex items-center gap-1'>
                        <FaUsers size={10} className='text-slate-400' />
                        {giveaway.participantCount || 0} iştirakçı
                    </span>
                    <span className='inline-flex items-center gap-1'>
                        <FaTrophy size={10} className='text-warning-500' />
                        {giveaway.winnerCount || 1} qalib
                    </span>
                </div>

                {/* CTA — Phase 6A dual when active. Both still route to the
                    detail page; participate flow stays gated there. */}
                {giveaway.status === 'active' ? (
                    <div className='mt-2 grid grid-cols-2 gap-2'>
                        <span className='inline-flex items-center justify-center rounded-card bg-brand-600 px-2.5 py-2 text-xs font-bold text-white group-hover:bg-brand-700 transition-colors'>
                            İştirak et
                        </span>
                        <span className='inline-flex items-center justify-center gap-1 rounded-card bg-brand-50 px-2.5 py-2 text-xs font-semibold text-brand-700 group-hover:bg-brand-100 transition-colors'>
                            Ətraflı bax <FaArrowRight size={9} />
                        </span>
                    </div>
                ) : (
                    <span className='mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-card bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 group-hover:bg-brand-600 group-hover:text-white transition-colors'>
                        Ətraflı bax <FaArrowRight size={9} />
                    </span>
                )}
            </div>
        </Link>
    )
}

export default GiveawayCard
